import type { FC, JSX } from "react";
import { isValidElement, useRef, useState } from "react";

import { isEqual, noop } from "lodash";
import styled from "styled-components";

import { DropdownButton } from "@similarweb/ui-components";

import { MultiSelectDropdownContainerStateless } from "app/components/dashboard/widget-wizard/components/MultiSelectDropdownContainerStateless";
import categoryService, { ALL } from "app/common/services/categoryService";
import { DropdownItem } from "pro-features/components/MultiCategoriesChipDown/src/MultiCategoryChipdownStyles";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { translate } from "app/filters/i18nFilter";
import type { ICategoryItem } from "app/common/keyword-research/keyword-search-ads/categoryOptionsUtils";
import { getCategoriesOptions } from "app/common/keyword-research/keyword-search-ads/categoryOptionsUtils";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";

const CategoriesDropdownContainer = styled.div`
    margin-bottom: 56px;
`;

const renderDropDownItem = (item: ICategoryItem): JSX.Element => {
    if (isValidElement(item)) {
        return item;
    }

    return (
        <DropdownItem
            key={item.id}
            id={item.id}
            icon={item.icon}
            name={item.text}
            isChild={false}
            parentId={item.parentId}
            text={item.text}
        />
    );
};

interface IMultiSelectCategoriesFilter {
    onApprove: (selectedCategories: ICategoryItem[]) => void;
    onDropDownToggle?: (isOpen: boolean) => void;
    selectedCategories: string;
}

export const MultiSelectCategoriesFilter: FC<IMultiSelectCategoriesFilter> = ({
    onApprove,
    onDropDownToggle = noop,
    selectedCategories = "",
}) => {
    const wereChangesMade = useRef(false);
    const categoryItemsSnapshot = useRef<(ICategoryItem & { selected: boolean })[]>(undefined);
    const [categoryItems, setCategoryItems] = useState<(ICategoryItem & { selected: boolean })[]>(() => {
        const categories = categoryService.getCategories().filter((category) => category.id !== ALL);
        const categoryOptions = getCategoriesOptions(categories);
        const initialSelectedCategories = selectedCategories?.split(",");
        return categoryOptions.map((category) => ({
            ...category,
            selected: initialSelectedCategories?.includes(category.id),
        }));
    });

    const getButtonComponent = () => {
        let buttonText;
        const selectedCategoriesCount = categoryItems.filter((category) => category.selected).length;
        if (selectedCategoriesCount === 0) {
            buttonText = translate("home.dashboards.wizard.categories.button.text");
        } else if (selectedCategoriesCount === 1) {
            buttonText = categoryItems.find((category) => category.selected).text;
        } else {
            buttonText = `${translate("search.industries.button")} (${selectedCategoriesCount})`;
        }

        return <DropdownButton key={buttonText}>{buttonText}</DropdownButton>;
    };

    const onDropdownItemClick = ({ id: selectedItemId }) => {
        if (!categoryItemsSnapshot.current) {
            categoryItemsSnapshot.current = categoryItems;
        }
        const currentCategoryItem = { ...categoryItems.find((item) => item.id === selectedItemId) };
        currentCategoryItem.selected = !currentCategoryItem.selected;

        const currentCategoryItemIndex = categoryItems.findIndex((item) => item.id === currentCategoryItem.id);
        const newCategoryItems = [...categoryItems];
        newCategoryItems[currentCategoryItemIndex] = currentCategoryItem;

        if (!isEqual(categoryItemsSnapshot.current, newCategoryItems)) {
            wereChangesMade.current = true;
        } else {
            wereChangesMade.current = false;
        }

        setCategoryItems(newCategoryItems);
    };

    const onApply = () => {
        if (!wereChangesMade.current) {
            return;
        }

        const updatedSelectedCategories = categoryItems.filter((category) => category.selected);

        // reset categoryItemsSnapshot and wereChangesMade
        categoryItemsSnapshot.current = undefined;
        wereChangesMade.current = false;

        onApprove(updatedSelectedCategories);
    };

    const selectedItemIds = categoryItems.reduce((itemIds, item) => {
        if (item.selected) {
            return { ...itemIds, [item.id]: true };
        }
        return itemIds;
    }, {});

    return (
        <FlexColumn className="dynamicFilter">
            <h5>
                <I18n>home.dashboards.wizard.filters.category</I18n>
            </h5>
            <CategoriesDropdownContainer>
                <MultiSelectDropdownContainerStateless
                    onItemClick={onDropdownItemClick}
                    selectedItems={selectedItemIds}
                    onFooterButtonClick={onApply}
                    onClickOutside={onApply}
                    onToggleDropdown={onDropDownToggle}
                    searchPlaceHolder={translate("home.dashboards.wizard.categories.search.placeholder")}
                    width={264}
                >
                    {[getButtonComponent(), ...categoryItems.map(renderDropDownItem)]}
                </MultiSelectDropdownContainerStateless>
            </CategoriesDropdownContainer>
        </FlexColumn>
    );
};
